import { Navigate, useRoutes } from 'react-router-dom';
// auth
import AuthGuard from '@raas/shared/src/auth/AuthGuard';
import GuestGuard from '@raas/shared/src/auth/GuestGuard';
// layouts
import CompactLayout from '@raas/shared/src/layouts/compact';
import { LegalProvisionContextProvider } from '@raas/shared/src/contexts/LegalProvisionContextProvider';
import { DeltaContextProvider } from '@raas/shared/src/contexts/DeltaContextProvider';
import { UserContextProvider } from '@raas/shared/src/features/user/contexts/UserContextProvider';
import { SubscriptionContextProvider } from '@raas/shared/src/features/subscription/contexts/SubscriptionContextProvider';
import { RegulationContextProvider } from '@raas/shared/src/features/regulation/contexts/RegulationContextProvider';
import { RegulationListContextProvider } from '@raas/shared/src/features/regulation/contexts/RegulationListContextProvider';
import { RegulationMyListContextProvider } from '@raas/shared/src/features/regulation/contexts/RegulationMyListContextProvider';
import { MyOrganizationProvider } from '@raas/shared/src/features/organization/contexts/MyOrganizationProvider';
import { BundlingContextProvider } from '@raas/shared/src/features/bundling/contexts/BundlingContextProvider';
import DashboardLayout from '../layouts/dashboard';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
//
import {
  Page404,
  LoginPage,
  UserChangePassword,
  LandingPageOverviewPage,
  LegalProvisionDetailsPage,
  LegalProvisionRequestPage,
  LegalProvisionEditPage,
  LegalRequirementCreatePage,
  LegalRequirementEditPage,
  LegalRequirementListPage,
  DocumentElementCreatePage,
  DocumentElementEditPage,
  DocumentElementViewPage,
  TagGroupListPage,
  TagGroupEditPage,
  TagGroupCreatePage,
  TagListPage,
  TagEditPage,
  TagCreatePage,
  DeltaPage,
  ExportsListPage,
  ExportsEditPage,
  AlertsListPage,
  AlertsEditPage,
  LegalProvisionWithTranslationPage,
  RegulationListPage,
  IntegrationPage,
  UserProfilePage,
  SignUpPage,
  BundlingListPage,
  AttributeListPage,
  RegulationContentPage,
  RegulationPropertyListPage,
} from './elements';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      path: '/',
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage requiredPermission="EXTERNAL_ACCESS" />
            </GuestGuard>
          ),
        },
        {
          path: 'signup',
          element: (
            <GuestGuard>
              <SignUpPage requiredPermission="EXTERNAL_ACCESS" />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: '/dashboard',
      element: (
        <AuthGuard requiredPermission="EXTERNAL_ACCESS">
          <LegalProvisionContextProvider requiredPermission="EXTERNAL_ACCESS">
            <UserContextProvider interfaceType="INTERNAL_ACCESS">
              <MyOrganizationProvider>
                <RegulationListContextProvider>
                  <RegulationMyListContextProvider>
                    <RegulationContextProvider>
                      <BundlingContextProvider>
                        <SubscriptionContextProvider>
                          <DeltaContextProvider>
                            <DashboardLayout />
                          </DeltaContextProvider>
                        </SubscriptionContextProvider>
                      </BundlingContextProvider>
                    </RegulationContextProvider>
                  </RegulationMyListContextProvider>
                </RegulationListContextProvider>
              </MyOrganizationProvider>
            </UserContextProvider>
          </LegalProvisionContextProvider>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: 'overview',
          children: [{ element: <LandingPageOverviewPage replace />, index: true }],
        },
        {
          path: 'legal-provision',
          children: [
            { element: <Navigate to="/dashboard/legal-provision/list" replace />, index: true },
            { path: ':id/details', element: <LegalProvisionDetailsPage /> },
            { path: 'new', element: <LegalProvisionRequestPage /> },
            { path: ':id/edit', element: <LegalProvisionEditPage /> },
          ],
        },
        {
          path: 'regulation',
          children: [
            { element: <Navigate to="/dashboard/regulation/list" replace />, index: true },
            { path: 'list', element: <RegulationListPage /> },
            { path: ':id/properties', element: <RegulationPropertyListPage /> },
            { path: ':id/tree', element: <LegalProvisionDetailsPage /> },
            { path: ':id/content', element: <RegulationContentPage /> },
          ],
        },
        {
          path: 'translation',
          children: [
            {
              element: <Navigate to="/dashboard/translation/current-legal-provision" replace />,
              index: true,
            },
            { path: 'current-legal-provision', element: <LegalProvisionWithTranslationPage /> },
          ],
        },
        {
          path: 'document-element',
          children: [
            { path: 'new', element: <DocumentElementCreatePage /> },
            { path: ':id/edit', element: <DocumentElementEditPage /> },
            { path: ':id/view', element: <DocumentElementViewPage /> },
          ],
        },
        {
          path: 'legal-requirement',
          children: [
            { element: <Navigate to="/dashboard/legal-requirement/list" replace />, index: true },
            { path: 'list', element: <LegalRequirementListPage /> },
            { path: 'new', element: <LegalRequirementCreatePage /> },
            { path: ':id/edit', element: <LegalRequirementEditPage /> },
          ],
        },
        {
          path: 'tag-group',
          children: [
            {
              element: <Navigate to="/dashboard/tag-group/list" replace />,
              index: true,
            },
            { path: 'list', element: <TagGroupListPage /> },
            { path: 'new', element: <TagGroupCreatePage /> },
            { path: ':id/edit', element: <TagGroupEditPage /> },
          ],
        },
        {
          path: 'tag',
          children: [
            {
              element: <Navigate to="/dashboard/tag/list" replace />,
              index: true,
            },
            { path: 'list', element: <TagListPage /> },
            { path: 'new', element: <TagCreatePage /> },
            { path: ':id/edit', element: <TagEditPage /> },
          ],
        },
        {
          path: 'user',
          children: [
            { path: 'change-password', element: <UserChangePassword /> },
            { path: 'profile', element: <UserProfilePage /> },
          ],
        },
        {
          path: 'delta',
          children: [
            {
              element: <Navigate to="/dashboard/delta/lp" replace />,
              index: true,
            },
            {
              path: 'lp',
              element: <DeltaPage />,
            },
          ],
        },
        {
          path: 'exports',
          children: [
            {
              path: 'list',
              element: <ExportsListPage />,
            },
            {
              path: 'new',
              element: <ExportsEditPage create isExternalView />,
            },
            {
              path: ':id/edit',
              element: <ExportsEditPage create={false} isExternalView />,
            },
          ],
        },
        {
          path: 'alerts',
          children: [
            {
              path: 'list',
              element: <AlertsListPage />,
            },
            {
              path: 'new',
              element: <AlertsEditPage create />,
            },
            {
              path: ':id/edit',
              element: <AlertsEditPage create={false} />,
            },
          ],
        },
        {
          path: 'integration/search',
          element: <IntegrationPage />,
        },
        {
          path: 'alerts',
          children: [
            {
              path: 'list',
              element: <AlertsListPage />,
            },
            {
              path: 'new',
              element: <AlertsEditPage create />,
            },
            {
              path: ':id/edit',
              element: <AlertsEditPage create={false} />,
            },
          ],
        },
        {
          path: 'bundlings',
          children: [
            {
              element: <Navigate to="/dashboard/bundlings/list" replace />,
              index: true,
            },
            {
              path: 'list',
              element: <BundlingListPage />,
            },
          ],
        },
        {
          path: 'attributes',
          children: [
            {
              element: <Navigate to="/dashboard/attributes/list" replace />,
              index: true,
            },
            {
              path: 'list',
              element: <AttributeListPage />,
            },
          ],
        },
      ],
    },
    {
      element: <CompactLayout />,
      children: [{ path: '404', element: <Page404 /> }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
